import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "./init";
import { Announcement } from "@/types/announcement";
import { convertTimestampToDate } from "./firestore";

type AnnouncementDate = Date | Timestamp | string | number | null;
type AnnouncementWrite = Omit<Announcement, "id" | "createdAt" | "updatedAt" | "startAt" | "endAt"> & {
  startAt?: AnnouncementDate;
  endAt?: AnnouncementDate;
};

/**
 * Firestore 公告數據類型（包含 Timestamp）
 */
interface FirestoreAnnouncementData {
  title?: string;
  content?: string;
  isActive?: boolean;
  imageURL?: string;
  ctaText?: string;
  ctaLink?: string;
  displayMode?: string;
  targetAudience?: string[];
  startAt?: Timestamp | Date | null;
  endAt?: Timestamp | Date | null;
  createdAt?: Timestamp | Date | null;
  updatedAt?: Timestamp | Date | null;
  schemaVersion?: number;
}

/**
 * 轉換公告數據（處理 Timestamp）
 */
function convertAnnouncementData(
  data: FirestoreAnnouncementData,
  id: string
): Omit<Announcement, "id"> {
  return {
    title: data.title || "",
    content: data.content || "",
    isActive: data.isActive ?? true,
    imageURL: data.imageURL,
    ctaText: data.ctaText,
    ctaLink: data.ctaLink,
    displayMode: (data.displayMode as "once" | "everyTime" | "daily") || "once",
    targetAudience: data.targetAudience || [],
    startAt: convertTimestampToDate(data.startAt) as Date | undefined,
    endAt: convertTimestampToDate(data.endAt) as Date | undefined,
    createdAt: convertTimestampToDate(data.createdAt) as Date | undefined,
    updatedAt: convertTimestampToDate(data.updatedAt) as Date | undefined,
    schemaVersion: data.schemaVersion || 1,
  };
}

/**
 * 獲取所有公告
 */
export async function getAnnouncements(): Promise<Announcement[]> {
  try {
    const announcementsRef = collection(db, "announcements");
    let querySnapshot;
    let useClientSort = false;

    try {
      const q = query(announcementsRef, orderBy("createdAt", "desc"));
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      console.warn(
        "Failed to order by 'createdAt', using basic query with client-side sort:",
        orderError
      );
      querySnapshot = await getDocs(query(announcementsRef));
      useClientSort = true;
    }

    const announcements = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertAnnouncementData(doc.data(), doc.id),
    })) as Announcement[];

    // 如果沒有使用 orderBy，在客戶端排序
    if (useClientSort) {
      announcements.sort((a, b) => {
        const aTime = a.createdAt?.getTime() || 0;
        const bTime = b.createdAt?.getTime() || 0;
        return bTime - aTime; // 降序
      });
    }

    return announcements;
  } catch (error) {
    console.error("Error getting announcements:", error);
    throw error;
  }
}

/**
 * 獲取單個公告
 */
export async function getAnnouncement(
  id: string
): Promise<Announcement | null> {
  try {
    const announcementRef = doc(db, "announcements", id);
    const announcementSnap = await getDoc(announcementRef);

    if (announcementSnap.exists()) {
      return {
        id: announcementSnap.id,
        ...convertAnnouncementData(announcementSnap.data(), announcementSnap.id),
      } as Announcement;
    }

    return null;
  } catch (error) {
    console.error("Error getting announcement:", error);
    throw error;
  }
}

/**
 * 創建公告
 */
export async function createAnnouncement(
  data: Omit<AnnouncementWrite, "schemaVersion">
): Promise<string> {
  try {
    const announcementsRef = collection(db, "announcements");

    // 驗證必填欄位
    if (!data.title || !data.content) {
      throw new Error("標題和內容為必填");
    }

    const announcementData: {
      title: string;
      content: string;
      isActive: boolean;
      imageURL?: string;
      ctaText?: string;
      ctaLink?: string;
      displayMode: string;
      targetAudience?: string[];
      startAt?: Timestamp | null;
      endAt?: Timestamp | null;
      createdAt: Timestamp;
      updatedAt: Timestamp;
      schemaVersion: number;
    } = {
      title: data.title,
      content: data.content,
      isActive: data.isActive ?? true,
      displayMode: data.displayMode || "once",
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      schemaVersion: 1,
    };

    if (data.imageURL) announcementData.imageURL = data.imageURL;
    if (data.ctaText) announcementData.ctaText = data.ctaText;
    if (data.ctaLink) announcementData.ctaLink = data.ctaLink;
    if (data.targetAudience && data.targetAudience.length > 0)
      announcementData.targetAudience = data.targetAudience;
    if (data.startAt) {
      announcementData.startAt =
        data.startAt instanceof Date
          ? Timestamp.fromDate(data.startAt)
          : data.startAt instanceof Timestamp
          ? data.startAt
          : Timestamp.fromDate(new Date(data.startAt));
    }
    if (data.endAt) {
      announcementData.endAt =
        data.endAt instanceof Date
          ? Timestamp.fromDate(data.endAt)
          : data.endAt instanceof Timestamp
          ? data.endAt
          : Timestamp.fromDate(new Date(data.endAt));
    }

    const docRef = await addDoc(announcementsRef, announcementData);
    return docRef.id;
  } catch (error) {
    console.error("Error creating announcement:", error);
    throw error;
  }
}

/**
 * 更新公告
 */
export async function updateAnnouncement(
  id: string,
  data: Partial<Omit<AnnouncementWrite, "schemaVersion">>
): Promise<void> {
  try {
    const announcementRef = doc(db, "announcements", id);
    const updateData: {
      updatedAt: Timestamp;
      title?: string;
      content?: string;
      isActive?: boolean;
      imageURL?: string;
      ctaText?: string;
      ctaLink?: string;
      displayMode?: string;
      targetAudience?: string[];
      startAt?: Timestamp | null;
      endAt?: Timestamp | null;
    } = {
      updatedAt: Timestamp.now(),
    };

    // 只更新提供的欄位
    if (data.title !== undefined) updateData.title = data.title;
    if (data.content !== undefined) updateData.content = data.content;
    if (data.isActive !== undefined) updateData.isActive = data.isActive;
    if (data.imageURL !== undefined) updateData.imageURL = data.imageURL;
    if (data.ctaText !== undefined) updateData.ctaText = data.ctaText;
    if (data.ctaLink !== undefined) updateData.ctaLink = data.ctaLink;
    if (data.displayMode !== undefined)
      updateData.displayMode = data.displayMode;
    if (data.targetAudience !== undefined)
      updateData.targetAudience = data.targetAudience;
    if (data.startAt !== undefined) {
      if (data.startAt === null) {
        updateData.startAt = null;
      } else {
        updateData.startAt =
          data.startAt instanceof Date
            ? Timestamp.fromDate(data.startAt)
            : data.startAt instanceof Timestamp
            ? data.startAt
            : Timestamp.fromDate(new Date(data.startAt));
      }
    }
    if (data.endAt !== undefined) {
      if (data.endAt === null) {
        updateData.endAt = null;
      } else {
        updateData.endAt =
          data.endAt instanceof Date
            ? Timestamp.fromDate(data.endAt)
            : data.endAt instanceof Timestamp
            ? data.endAt
            : Timestamp.fromDate(new Date(data.endAt));
      }
    }

    await updateDoc(announcementRef, updateData);
  } catch (error) {
    console.error("Error updating announcement:", error);
    throw error;
  }
}

/**
 * 刪除公告
 */
export async function deleteAnnouncement(id: string): Promise<void> {
  try {
    const announcementRef = doc(db, "announcements", id);
    await deleteDoc(announcementRef);
  } catch (error) {
    console.error("Error deleting announcement:", error);
    throw error;
  }
}

